// The merchant-configuration form, shared by the TWO surfaces that set one up.
//
// It was the builder's Advanced → Payments screen and nothing else. A
// `payment_settings` block now puts the same form inside the running
// application, for a customer's own administrator who may hold no platform
// permission at all — so the form moved here rather than being written twice.
// A credential form that existed in two copies would be two places to get the
// write-only handling of a secret wrong.
//
// WHICH ENDPOINTS it talks to is the ONLY difference between the surfaces, and
// it arrives as `api`: the builder passes `/api/payment-gateways/…` (DEPLOY,
// platform-only, alongside the ledger), the block passes
// `/api/app-gateway-settings/…` (owner or FUNC-ADMIN, admitted on a customer's
// domain, configuration only). Everything about what a field means, what is
// masked and what a save sends is decided here, once.
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  AlertTriangle, Check, Copy, CreditCard, Loader2, RefreshCw, ShieldCheck, Trash2,
} from 'lucide-react'
import type {
  AccountModel, GatewayListResponse, GatewayMode, GatewayProvider,
  GatewayUpdate, PaymentGateway,
} from '@/api/appPayments'

const PROVIDER_LABEL: Record<string, string> = {
  razorpay: 'Razorpay',
  stripe: 'Stripe',
}

function errText(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

/** The three writes the form makes. Each surface supplies its own endpoints;
 *  see the note above for why that is the only thing they differ in. */
export interface GatewaySetupApi {
  save: (project: string, body: GatewayUpdate) => Promise<unknown>
  /** A REAL call to the gateway, so wrong keys surface here and not at the
   *  first customer. `ok: false` carries the gateway's own wording. */
  test: (project: string, provider: GatewayProvider) => Promise<{
    ok: boolean; error?: string; verified_at?: string | null
  }>
  remove: (project: string, provider: GatewayProvider) => Promise<unknown>
  /** Cash on delivery is a PROJECT setting, saved on its own: it needs no
   *  gateway, so it must be switchable before (or without) any gateway row. */
  setCod: (project: string, enabled: boolean) => Promise<boolean>
}

/** An empty gateway form for a provider the project has not configured. */
export function blankGateway(provider: GatewayProvider): PaymentGateway {
  return {
    id: 0, provider, mode: 'test', account_model: 'own_keys', enabled: false,
    key_id: '', key_secret_masked: '', webhook_secret_masked: '',
    has_key_secret: false, has_webhook_secret: false, linked_account_id: '',
    webhook_url: '', currency: provider === 'razorpay' ? 'INR' : 'USD',
    capture_mode: 'auto', methods_allowed: [], cod_enabled: true,
    brand_name: '', brand_logo_url: '', brand_color: '',
    verified_at: null, last_error: '', updated_at: null,
  }
}

export function GatewaySetupForm({ project, data, provider, onProvider, gateway, onSaved, api }: {
  project: string
  data: GatewayListResponse | null
  provider: GatewayProvider
  onProvider: (p: GatewayProvider) => void
  gateway: PaymentGateway
  onSaved: () => Promise<void> | void
  api: GatewaySetupApi
}) {
  // The form is seeded from the saved row and reset whenever the row changes
  // (a save, a provider switch). Secrets start EMPTY, never as the mask —
  // sending a mask back would be sending a value the user cannot see.
  const [form, setForm] = useState<GatewayUpdate>({ provider })
  const [keySecret, setKeySecret] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [busy, setBusy] = useState<'' | 'save' | 'test' | 'delete' | 'cod'>('')
  // The project's COD answer. Absent from an older server's response ⇒ the
  // gateway's echo, else the default (on).
  const codOn = data?.cod_enabled ?? gateway.cod_enabled ?? true

  useEffect(() => {
    setForm({
      provider: gateway.provider,
      mode: gateway.mode,
      account_model: gateway.account_model,
      enabled: gateway.enabled,
      key_id: gateway.key_id,
      linked_account_id: gateway.linked_account_id,
      currency: gateway.currency,
      capture_mode: gateway.capture_mode,
      methods_allowed: gateway.methods_allowed,
      brand_name: gateway.brand_name,
      brand_logo_url: gateway.brand_logo_url,
      brand_color: gateway.brand_color,
    })
    setKeySecret('')
    setWebhookSecret('')
  }, [gateway])

  const set = <K extends keyof GatewayUpdate>(key: K, value: GatewayUpdate[K]) =>
    setForm(prev => ({ ...prev, [key]: value }))

  const save = async () => {
    setBusy('save')
    try {
      const body: GatewayUpdate = { ...form, provider }
      // Omitted ⇒ unchanged. Only send what the person actually typed.
      if (keySecret.trim()) body.key_secret = keySecret.trim()
      if (webhookSecret.trim()) body.webhook_secret = webhookSecret.trim()
      await api.save(project, body)
      toast.success('Payment setup saved')
      await onSaved()
    } catch (err) {
      toast.error(errText(err, 'Could not save the payment setup'))
    } finally {
      setBusy('')
    }
  }

  const test = async () => {
    setBusy('test')
    try {
      const result = await api.test(project, provider)
      if (result.ok) toast.success('Reached the gateway with these credentials')
      else toast.error(result.error || 'The gateway refused these credentials')
      await onSaved()
    } catch (err) {
      toast.error(errText(err, 'Could not reach the gateway'))
    } finally {
      setBusy('')
    }
  }

  const remove = async () => {
    if (!window.confirm(
      `Remove the ${PROVIDER_LABEL[provider]} setup for this application?\n\n`
      + 'The stored keys are deleted. Orders already taken keep their records.',
    )) return
    setBusy('delete')
    try {
      await api.remove(project, provider)
      toast.success('Removed')
      await onSaved()
    } catch (err) {
      toast.error(errText(err, 'Could not remove that gateway'))
    } finally {
      setBusy('')
    }
  }

  const toggleCod = async (enabled: boolean) => {
    setBusy('cod')
    try {
      await api.setCod(project, enabled)
      toast.success(enabled ? 'Cash on delivery is offered'
        : 'Cash on delivery switched off')
      await onSaved()
    } catch (err) {
      toast.error(errText(err, 'Could not change cash on delivery'))
    } finally {
      setBusy('')
    }
  }

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success('Copied')
    } catch {
      toast('Select the value to copy it')
    }
  }

  const brokered = form.account_model === 'brokered'
  const methods = form.methods_allowed || []

  return (
    <div style={{ marginLeft: 44 }}>
      {/* Which gateway. Both are real here — unlike the platform's own rail,
          where only Razorpay carries subscriptions. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(data?.providers || []).map(p => (
          <button
            key={p.name}
            className={`btn btn-sm ${p.name === provider ? '' : 'btn-ghost'}`}
            onClick={() => onProvider(p.name as GatewayProvider)}
          >
            {PROVIDER_LABEL[p.name] || p.name}
            {data?.gateways.some(g => g.provider === p.name && g.enabled) && (
              <Check size={13} style={{ marginLeft: 6, verticalAlign: -2 }} />
            )}
          </button>
        ))}
      </div>

      {gateway.id > 0 && (
        <StatusBanner gateway={gateway} />
      )}

      {/* The webhook comes FIRST. An unconfigured callback is the single most
          common reason a real payment never reaches the application. */}
      {gateway.id > 0 && (
        <div className="card" style={{ padding: 18, marginBottom: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            Webhook URL
          </div>
          <p style={{
            fontSize: 13.5, color: 'var(--color-text-secondary)', margin: '0 0 10px',
          }}>
            {provider === 'razorpay'
              ? <>Add this as a webhook in Razorpay&apos;s dashboard (Account &amp;
                Settings → Webhooks) with a secret you choose, and enter that
                same secret below.</>
              : <>Paste this into {PROVIDER_LABEL[provider]}&apos;s dashboard and
                copy the signing secret it gives you into the field below.</>} This is the
            path a payment actually takes: the customer&apos;s browser may close
            the moment their bank approves, and then only this callback tells
            the application it was paid.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-input" readOnly value={gateway.webhook_url}
                   style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }} />
            <button className="btn btn-sm btn-ghost"
                    onClick={() => copy(gateway.webhook_url)}>
              <Copy size={14} /> Copy
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 20, marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
          {PROVIDER_LABEL[provider]} credentials
        </div>

        <Row>
          <Field label="Mode" hint="Test keys charge nothing real.">
            <select className="form-input" value={form.mode || 'test'}
                    onChange={e => set('mode', e.target.value as GatewayMode)}>
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </Field>
          <Field label="Currency" hint="What this shop prices in.">
            <input className="form-input" maxLength={3} value={form.currency || ''}
                   onChange={e => set('currency', e.target.value.toUpperCase())} />
          </Field>
          <Field label="Capture"
                 hint="Manual authorises now and charges when you ship.">
            <select className="form-input" value={form.capture_mode || 'auto'}
                    onChange={e => set('capture_mode',
                      e.target.value as 'auto' | 'manual')}>
              <option value="auto">Capture immediately</option>
              <option value="manual">Authorise only</option>
            </select>
          </Field>
        </Row>

        <Row>
          <Field label={provider === 'stripe' ? 'Publishable key' : 'Key ID'}
                 hint="Publishable — the browser sees it.">
            {/* A browser password manager reads an unnamed text box beside a
                password box as a USERNAME and fills it with the person's
                e-mail. That is the worst field on this form to get a stray
                value into: `key_id` is PUBLISHABLE — it is stored in the
                clear and handed to the browser to open the gateway — so an
                autofilled address would be published. `off` plus a name the
                heuristics do not recognise is what stops it. */}
            <input className="form-input" value={form.key_id || ''}
                   name="gateway-key-id" autoComplete="off"
                   autoCorrect="off" autoCapitalize="off" spellCheck={false}
                   onChange={e => set('key_id', e.target.value)}
                   placeholder={provider === 'stripe' ? 'pk_test_…' : 'rzp_test_…'} />
          </Field>
          <Field
            label={provider === 'stripe' ? 'Secret key' : 'Key secret'}
            hint={gateway.has_key_secret
              ? `Stored (${gateway.key_secret_masked}). Leave blank to keep it.`
              : 'Never shown again once saved.'}
          >
            {/* `new-password`, not `off`: Chrome ignores `off` on a password
                input and offers a saved credential anyway. Without this an
                administrator can save their own Twasta password as the
                merchant secret without ever seeing it happen. */}
            <input className="form-input" type="password" value={keySecret}
                   name="gateway-key-secret" autoComplete="new-password"
                   onChange={e => setKeySecret(e.target.value)}
                   placeholder={gateway.has_key_secret ? '••••••••' : 'sk_… / secret'} />
          </Field>
        </Row>

        <Row>
          <Field
            label="Webhook signing secret"
            hint={gateway.has_webhook_secret
              ? `Stored (${gateway.webhook_secret_masked}). Leave blank to keep it.`
              : provider === 'razorpay'
                // Razorpay issues only a key id + key secret; the webhook
                // secret is one YOU choose while adding the webhook, which is
                // why people go looking for it and cannot find it.
                ? 'Razorpay does not issue this — you make it up. In the Razorpay '
                  + 'Dashboard → Account & Settings → Webhooks → Add New Webhook, '
                  + 'paste the URL above, type any strong secret, tick '
                  + 'payment.captured, payment.failed and order.paid, then enter '
                  + 'the same secret here.'
                : 'Stripe Dashboard → Developers → Webhooks → Add endpoint with the '
                  + 'URL above, then reveal its signing secret (whsec_…).'}
          >
            <input className="form-input" type="password" value={webhookSecret}
                   name="gateway-webhook-secret" autoComplete="new-password"
                   onChange={e => setWebhookSecret(e.target.value)}
                   placeholder={gateway.has_webhook_secret ? '••••••••'
                     : provider === 'razorpay' ? 'The secret you set on the Razorpay webhook' : 'whsec_…'} />
          </Field>
        </Row>

        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          <button className="btn btn-sm" onClick={save} disabled={busy !== ''}>
            {busy === 'save' ? <Loader2 size={14} className="spin" /> : null} Save
          </button>
          {gateway.id > 0 && (
            <button className="btn btn-sm btn-ghost" onClick={test} disabled={busy !== ''}>
              {busy === 'test'
                ? <Loader2 size={14} className="spin" />
                : <RefreshCw size={14} />} Test connection
            </button>
          )}
          {gateway.id > 0 && (
            <button className="btn btn-sm btn-ghost" onClick={remove}
                    disabled={busy !== ''}
                    style={{ marginLeft: 'auto', color: 'var(--color-error, #dc2626)' }}>
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
      </div>

      {/* Settlement. Only offered when the instance actually brokers. */}
      <div className="card" style={{ padding: 20, marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
          Where the money settles
        </div>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
          <input type="radio" checked={!brokered}
                 onChange={() => set('account_model', 'own_keys' as AccountModel)} />
          <span style={{ fontSize: 14 }}>
            <strong>Your own account.</strong>{' '}
            <span style={{ color: 'var(--color-text-secondary)' }}>
              Customers pay {PROVIDER_LABEL[provider]} directly with the keys
              above; the platform takes nothing and never touches the funds.
            </span>
          </span>
        </label>
        <label style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          opacity: data?.brokering_enabled ? 1 : 0.55,
        }}>
          <input type="radio" checked={brokered} disabled={!data?.brokering_enabled}
                 onChange={() => set('account_model', 'brokered' as AccountModel)} />
          <span style={{ fontSize: 14 }}>
            <strong>Collected for you, then settled.</strong>{' '}
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {data?.brokering_enabled
                ? `The platform collects and transfers to your linked account, `
                  + `keeping ${data.platform_fee_percent}%.`
                : 'Not offered on this instance — use your own account.'}
            </span>
          </span>
        </label>
        {brokered && (
          <div style={{ marginTop: 12 }}>
            <Field
              label="Linked account ID"
              hint={`Created in the platform operator's ${PROVIDER_LABEL[provider]} `
                    + 'dashboard once your business details are verified. The '
                    + 'platform performs the split; it does not do that onboarding '
                    + 'for you.'}
            >
              {/* Money-routing field: same reason as `key_id` above. */}
              <input className="form-input" value={form.linked_account_id || ''}
                     name="gateway-linked-account" autoComplete="off"
                     onChange={e => set('linked_account_id', e.target.value)}
                     placeholder={provider === 'stripe' ? 'acct_…' : 'acc_…'} />
            </Field>
          </div>
        )}
      </div>

      {/* What the storefront may offer. */}
      <div className="card" style={{ padding: 20, marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
          What customers can pay with
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 12 }}>
          {(data?.known_methods || []).map(m => (
            <label key={m} style={{ display: 'flex', gap: 6, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={methods.includes(m)}
                onChange={e => set('methods_allowed', e.target.checked
                  ? [...methods, m]
                  : methods.filter(x => x !== m))}
              />
              {m}
            </label>
          ))}
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          Tick nothing to offer whatever the gateway has enabled on your account.
        </p>
        {/* Saved at once and for the whole APPLICATION, not with this
            gateway's Save: COD needs no gateway, so it must work before any
            row exists. */}
        <label style={{ display: 'flex', gap: 8, fontSize: 14, alignItems: 'center' }}>
          <input type="checkbox" checked={codOn} disabled={busy !== ''}
                 onChange={e => void toggleCod(e.target.checked)} />
          {busy === 'cod' && <Loader2 size={13} className="spin" />}
          <span>
            Offer <strong>cash on delivery</strong>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {' '}— on by default; needs no gateway, and works even with none
              connected. Each order&apos;s payment action must say what status a
              COD order takes (<code>cod_status_value</code>).
            </span>
          </span>
        </label>
      </div>

      {/* Brand shown inside the gateway's own modal. */}
      <div className="card" style={{ padding: 20, marginBottom: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
          How your shop appears at checkout
        </div>
        <Row>
          <Field label="Business name">
            <input className="form-input" value={form.brand_name || ''}
                   onChange={e => set('brand_name', e.target.value)} />
          </Field>
          <Field label="Logo URL">
            <input className="form-input" value={form.brand_logo_url || ''}
                   onChange={e => set('brand_logo_url', e.target.value)} />
          </Field>
          <Field label="Accent colour">
            <input className="form-input" value={form.brand_color || ''}
                   onChange={e => set('brand_color', e.target.value)}
                   placeholder="#14307A" />
          </Field>
        </Row>
      </div>

      {/* Enabling is last, and refused while anything needed is missing. */}
      <div className="card" style={{ padding: 18, marginBottom: 18 }}>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={!!form.enabled}
                 onChange={e => set('enabled', e.target.checked)} />
          <span style={{ fontSize: 14 }}>
            <strong>Take payments with this gateway.</strong>{' '}
            <span style={{ color: 'var(--color-text-secondary)' }}>
              The application will offer online payment as soon as this is on.
              It cannot be switched on until the key id, key secret and webhook
              secret are all stored.
            </span>
          </span>
        </label>
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-sm" onClick={save} disabled={busy !== ''}>
            {busy === 'save' ? <Loader2 size={14} className="spin" /> : null} Save
          </button>
        </div>
      </div>
    </div>
  )
}

function StatusBanner({ gateway }: { gateway: PaymentGateway }) {
  const ok = !!gateway.verified_at
  const problem = gateway.last_error
  return (
    <div className="card" style={{
      padding: 14, marginBottom: 18, display: 'flex', gap: 10,
      borderLeft: `3px solid ${problem
        ? 'var(--color-error, #dc2626)'
        : ok ? 'var(--color-success, #16a34a)' : 'var(--color-warning, #d97706)'}`,
    }}>
      {problem
        ? <AlertTriangle size={18} style={{ color: 'var(--color-error, #dc2626)', flexShrink: 0 }} />
        : ok
          ? <ShieldCheck size={18} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
          : <CreditCard size={18} style={{ color: 'var(--color-warning, #d97706)', flexShrink: 0 }} />}
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
        {problem
          ? <>The last attempt to reach the gateway failed: <strong>{problem}</strong></>
          : ok
            ? <>Credentials verified against the gateway.{' '}
                {gateway.enabled
                  ? 'This application is taking payments.'
                  : 'Switch it on below to start taking payments.'}</>
            : <>These credentials have not been tested yet. Use{' '}
                <strong>Test connection</strong> before going live.</>}
      </div>
    </div>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
      {children}
    </div>
  )
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode
}) {
  return (
    <div style={{ flex: '1 1 240px', minWidth: 200 }}>
      <label className="form-label">{label}</label>
      {children}
      {hint && (
        <div style={{
          fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4,
        }}>{hint}</div>
      )}
    </div>
  )
}

// ── Ledger ──────────────────────────────────────────────────────────────────

